import Preact from 'preact'
import { Router } from 'preact-router'
import {
  HomeRoute,
  GroupsRoute,
  KnockoutRoute,
  ScheduleRoute,
  MatchRoute,
  GoalShowRoute
} from './Routes'
import { connect, DispatchProp } from 'preact-redux'
import { connect as contextConnect } from '../../lib/provider'
import { Header } from './common/Header'
import { Footer } from './common/Footer'
import { MatchNavContainer } from '../containers/MatchNavContainer'
import { Redirect } from './lib/Redirect'

import styles from './App.css'

import {
  IAppState,
  IFeatures,
  IScheduleMatches,
  ICredentials,
  IFirebaseInstance
} from '../../types'
import { refetch } from '../actions/data'
import { handleRouteChange } from '../actions/general'
import { PusherManager } from '../../lib/pusher-client'
import { generatePusherUrl } from '../../lib/generate-pusher-urls'
import { bem } from '../../../../node_modules/@nrk/dh-utils'

const { h, Component } = Preact

interface IStateProps {
  BASE_URL?: string
  INITIAL_URL?: string
  currentUrl?: string
  credentials: ICredentials
  features: IFeatures
  schedule: IScheduleMatches
}

interface IStateActions {
  handleRouteChange: (url: string) => void
  refetch: (id: string) => () => void
}

interface IGaProps {
  sendGaEvent?: (event: any) => void
}

interface IOnChange {
  url: string
  previous?: any
}

type IProps = IStateProps & IStateActions & IGaProps

interface IState {
  firebaseInstance: IFirebaseInstance
}

function getTitleByURL(url: string) {
  switch (true) {
    case url.indexOf('/grupper/') > -1:
      return 'Grupper – FIFA Fotball-VM 2018 – NRK'
    case url.indexOf('/kamper/') > -1:
      return 'Kamper – FIFA Fotball-VM 2018 – NRK'
    case url.indexOf('/kamp/') > -1:
      return 'Kamp – FIFA Fotball-VM 2018 – NRK'
    case url.indexOf('/klipp/') > -1:
      return 'Klipp – FIFA Fotball-VM 2018 – NRK'
    default:
      return 'FIFA Fotball-VM 2018 – NRK'
  }
}

class AppComponent extends Component<IProps, {}> {
  public headerTimeoutId: any
  public pusherInstance
  public state = {
    firebaseInstance: null
  }

  public componentDidMount() {
    const { firebase, pusher } = this.props.credentials
    const { apiKey, cluster } = pusher
    this.pusherInstance = new PusherManager(apiKey, cluster)
    this.pusherSubscribe()
  }

  public componentWillUnmount() {
    if (this.pusherInstance) return
    this.pusherInstance.unsubscribeAll()
  }

  public pusherSubscribe = () => {
    const { schedule, credentials } = this.props
    const { baseUrl } = credentials.pusher
    if (!this.pusherInstance || !schedule.data || !schedule.data.length) return
    this.pusherInstance.subscribe('my-channel', this.props.refetch('1316021'))
    schedule.data.forEach(scheduleItem => {
      const { id: matchId, group } = scheduleItem
      const { id: groupId } = group
      const pusherUrl = generatePusherUrl(matchId, groupId, baseUrl)
      this.pusherInstance.subscribe(pusherUrl, this.props.refetch(matchId))
    })
  }

  public waitForHeaderAndSetFocus() {
    const setFocusOrWait = () => {
      const header = document.querySelector('h1')

      this.headerTimeoutId = null

      if (header && header.outerHTML.length) {
        header.setAttribute('tabindex', '-1')
        header.focus()
      } else {
        this.headerTimeoutId = setTimeout(setFocusOrWait, 16)
      }
    }

    setFocusOrWait()
  }

  public handleRouteChange = ({ url }: IOnChange) => {
    this.props.handleRouteChange(url)

    setTimeout(() => {
      if (typeof window !== 'undefined') {
        if (this.props.sendGaEvent) {
          this.props.sendGaEvent({ action: 'navigate', label: url })
        }

        document.title = getTitleByURL(url)

        if (this.headerTimeoutId) {
          clearTimeout(this.headerTimeoutId)
        }

        this.waitForHeaderAndSetFocus()

        window.scrollTo(0, 0)
      }
    }, 0)
  }

  public render() {
    const { BASE_URL, INITIAL_URL, currentUrl, features } = this.props

    const links = [
      { text: 'Hjem', href: `${BASE_URL}/` },
      { text: 'Kamper', href: `${BASE_URL}/kamper/` },
      { text: 'Sluttspill', href: `${BASE_URL}/sluttspill/` },
      { text: 'Grupper', href: `${BASE_URL}/grupper/` },
      features.maalshow && { text: 'Klipp', href: `${BASE_URL}/klipp/` },
      {
        text: 'Manager',
        href: `https://vm-manager.nrk.no/landing-page`,
        isExternal: true
      }
    ].filter(Boolean)

    return (
      <div class={styles.app}>
        <Header BASE_URL={BASE_URL} links={links} />
        <div class={bem(styles.app__content, { white: isWhite(currentUrl) })}>
          <div class={styles['app__match-nav-container']}>
            <MatchNavContainer />
          </div>
          <Router url={currentUrl} onChange={this.handleRouteChange}>
            <ScheduleRoute path={`${BASE_URL}/kamper/`} />
            <GroupsRoute path={`${BASE_URL}/grupper/`} />
            <MatchRoute path={`${BASE_URL}/kamp/`} />
            <KnockoutRoute path={`${BASE_URL}/sluttspill/`} />
            <GoalShowRoute
              path={`${BASE_URL}/klipp/`}
              firebaseInstance={this.state.firebaseInstance}
            />
            <HomeRoute path={`${BASE_URL}/`} />
            <Redirect to={`${BASE_URL}/`} default />
          </Router>
        </div>
        <Footer />
      </div>
    )
  }
}

const isWhite = (currentUrl: string): boolean => {
  try {
    return currentUrl.includes('/klipp')
  } catch (err) {
    return false
  }
}

const mapStateToProps = ({ general, data }: IAppState): IStateProps => {
  const { features, urls, credentials } = general
  const { schedule } = data
  return {
    BASE_URL: urls.BASE_URL,
    INITIAL_URL: urls.INITIAL_URL,
    currentUrl: urls.currentUrl,
    credentials,
    features,
    schedule
  }
}

const mapDispatchToProps = (dispatch): IStateActions => ({
  handleRouteChange: url => dispatch(handleRouteChange(url)),
  refetch: (id: string) => () => {
    dispatch(refetch(id))
  }
})

const mapContextToProps = context => {
  if (context && context.sendGaEvent) {
    return {
      sendGaEvent: context.sendGaEvent
    }
  } else {
    return {}
  }
}

// @ts-ignore
export const App = connect<IStateProps, {}, void>(
  mapStateToProps,
  mapDispatchToProps
  // @ts-ignore I HAVE NO IDEA
)(contextConnect(mapContextToProps)(AppComponent))
